import React, { useState } from 'react';
import { useUserDBContext } from '../contexts/UserDBContext';
import { useBackgroundContext } from '../contexts/BackgroundContext';
import { useDiaporamaSettings } from '../contexts/DiaporamaSettingsContext';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import {
  FaFont,
  FaImage,
  FaShieldAlt,
  FaTextHeight,
  FaUpload,
  FaCogs,
  FaLock,
  FaWater
} from 'react-icons/fa';
import './style/EventSettings.css';
import { SUBSCRIPTION_ACCESS } from '../constants/subscriptionRules';

function SettingsMenu() {
  const { t } = useTranslation(); // Initialize the translation function
  const [openSetting, setOpenSetting] = useState(null);

  const { userDBData } = useUserDBContext();
  const subscription = userDBData?.subscriptions || 'P1';

  const {
    fontColor, setFontColor,
    bgColor, setBgColor,
    fontFamily, setFontFamily,
    fontSize, setFontSize,
    backgroundImage, setBackgroundImage,
    showScanMe, setShowScanMe,
    scanInterval, setScanInterval,
    transitionSpeed, setTransitionSpeed,
    showQrCorner, setShowQrCorner,
    bannerEnabled, setBannerEnabled,
    bannerPosition, setBannerPosition,
    bannerText, setBannerText,
    bannerShowBackground, setBannerShowBackground,
    bannerBgColor, setBannerBgColor,
    bannerTextColor, setBannerTextColor,
    bannerFontSize, setBannerFontSize,
    qrCornerPosition, setQrCornerPosition,
    qrCornerSize, setQrCornerSize,
    animationType, setAnimationType,
    animationEnabled, setAnimationEnabled,
    watermarkEnabled, setWatermarkEnabled
  } = useDiaporamaSettings();

  const { backgrounds } = useBackgroundContext();

  const hasAccess = (settingKey) => SUBSCRIPTION_ACCESS[settingKey]?.includes(subscription);

  const toggleSetting = (key) => {
    setOpenSetting(prev => prev === key ? null : key);
  };

  const renderSetting = (key, icon, label, content) => {
    const allowed = hasAccess(key);
    return (
      <>
        <a
          className={`toggleable ${openSetting === key ? 'open' : ''} ${!allowed ? 'disabled' : ''}`}
          onClick={() => allowed && toggleSetting(key)}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{icon} {label}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {!allowed && <FaLock style={{ fontSize: '14px' }} />}
            <span className="arrow">›</span>
          </span>
        </a>
        {allowed && openSetting === key && <div className="submenu">{content}</div>}
      </>
    );
  };

  return (
    <>
      <div className="settings-label">{t('eventSettings.settingsLabel')}</div>
      <hr />
      <div className="sidebar-section">
        {renderSetting('text', <FaFont />, t('eventSettings.text'), (
          <input type="color" value={fontColor} onChange={(e) => setFontColor(e.target.value)} />
        ))}
        {renderSetting('fond', <FaImage />, t('eventSettings.background'), (
          <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
        ))}
        {renderSetting('font', <FaShieldAlt />, t('eventSettings.policy'), (
          <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
            <option value="Arial">Arial</option>
            <option value="Georgia">Georgia</option>
            <option value="Verdana">Verdana</option>
            <option value="'Courier New', monospace">Courier</option>
          </select>
        ))}
        {renderSetting('taille', <FaTextHeight />, t('eventSettings.size'), (
          <input type="number" value={parseInt(fontSize)} onChange={(e) => setFontSize(`${e.target.value}px`)} />
        ))}
        {renderSetting('bgimage', <FaUpload />, t('eventSettings.importedBackground'), (
          <select value={backgroundImage} onChange={(e) => setBackgroundImage(e.target.value)}>
            <option value="">{t('eventSettings.none')}</option>
            {backgrounds.map((url, idx) => (
              <option key={idx} value={url}>{new URL(url).pathname.split('/').pop()}</option>
            ))}
          </select>
        ))}

        {renderSetting('animation', <FaCogs />, t('eventSettings.animation'), (
          <>
            <div className="transition-option">
              <label>{t('eventSettings.activate')}</label>
              <label className="switch">
                <input type="checkbox" checked={animationEnabled} onChange={() => setAnimationEnabled(!animationEnabled)} />
                <span className="slider round"></span>
              </label>
            </div>
            {animationEnabled && (
              <div className="transition-field">
                <label>{t('eventSettings.type')}</label>
                <select value={animationType} onChange={(e) => setAnimationType(e.target.value)}>
                  <option value="fade">{t('eventSettings.fade')}</option>
                  <option value="slide-left">{t('eventSettings.slideLeft')}</option>
                  <option value="slide-right">{t('eventSettings.slideRight')}</option>
                  <option value="zoom">{t('eventSettings.zoom')}</option>
                </select>
              </div>
            )}
          </>
        ))}

        {renderSetting('transition', <FaCogs />, t('eventSettings.transition'), (
          <>
            <div className="transition-option">
              <label>Scan Me</label>
              <label className="switch">
                <input type="checkbox" checked={showScanMe} onChange={() => setShowScanMe(!showScanMe)} />
                <span className="slider round"></span>
              </label>
            </div>
            {showScanMe && (
              <div className="transition-field">
                <label>{t('eventSettings.slidesBeforeScanMe')}</label>
                <input type="text" defaultValue={scanInterval} onBlur={(e) => {
                  const parsed = parseInt(e.target.value);
                  if (!isNaN(parsed)) setScanInterval(parsed);
                }} />
              </div>
            )}
            <div className="transition-field">
              <label>{t('eventSettings.transitionSpeed')}</label>
              <input type="text" defaultValue={transitionSpeed} onBlur={(e) => {
                const parsed = parseInt(e.target.value);
                if (!isNaN(parsed)) setTransitionSpeed(parsed);
              }} />
            </div>
          </>
        ))}

        {renderSetting('qr', <FaCogs />, t('eventSettings.qrCode'), (
          <>
            <div className="transition-option">
              <label>{t('eventSettings.displayQr')}</label>
              <label className="switch">
                <input type="checkbox" checked={showQrCorner} onChange={() => setShowQrCorner(!showQrCorner)} />
                <span className="slider round"></span>
              </label>
            </div>
            <div className="transition-field">
              <label>{t('eventSettings.position')}</label>
              <select value={qrCornerPosition} onChange={(e) => setQrCornerPosition(e.target.value)}>
                <option value="bottom-right">{t('eventSettings.bottomRight')}</option>
                <option value="bottom-left">{t('eventSettings.bottomLeft')}</option>
              </select>
            </div>
            <div className="transition-field">
              <label>{t('eventSettings.size')}</label>
              <input type="text" defaultValue={qrCornerSize} onBlur={(e) => {
                const parsed = parseInt(e.target.value);
                if (!isNaN(parsed)) setQrCornerSize(parsed);
              }} />
            </div>
          </>
        ))}

        {renderSetting('banner', <FaCogs />, t('eventSettings.banner'), (
          <>
            <div className="transition-option">
              <label>{t('eventSettings.activate')}</label>
              <label className="switch">
                <input type="checkbox" checked={bannerEnabled} onChange={() => setBannerEnabled(!bannerEnabled)} />
                <span className="slider round"></span>
              </label>
            </div>
            <div className="transition-field">
              <label>{t('eventSettings.position')}</label>
              <select value={bannerPosition} onChange={(e) => setBannerPosition(e.target.value)}>
                <option value="hautcentre">{t('eventSettings.topCenter')}</option>
                <option value="bascentre">{t('eventSettings.bottomCenter')}</option>
              </select>
            </div>
            <div className="transition-field">
              <label>{t('eventSettings.text')}</label>
              <input type="text" defaultValue={bannerText} onBlur={(e) => setBannerText(e.target.value)} />
            </div>
            <div className="transition-option">
              <label>{t('eventSettings.background')}</label>
              <label className="switch">
                <input type="checkbox" checked={bannerShowBackground} onChange={() => setBannerShowBackground(!bannerShowBackground)} />
                <span className="slider round"></span>
              </label>
            </div>
            {bannerShowBackground && (
              <div className="transition-field">
                <label>{t('eventSettings.backgroundColor')}</label>
                <input type="color" value={bannerBgColor} onChange={(e) => setBannerBgColor(e.target.value)} />
              </div>
            )}
            <div className="transition-field">
              <label>{t('eventSettings.textColor')}</label>
              <input type="color" value={bannerTextColor} onChange={(e) => setBannerTextColor(e.target.value)} />
            </div>
            <div className="transition-field">
              <label>{t('eventSettings.textSize')}</label>
              <input
                type="text"
                defaultValue={parseInt(bannerFontSize)}
                onBlur={(e) => {
                  const parsed = parseInt(e.target.value);
                  if (!isNaN(parsed)) setBannerFontSize(`${parsed}px`);
                }}
              />
            </div>
          </>
        ))}
        {renderSetting('watermark', <FaWater />, t('eventSettings.watermark') || 'Watermark', (
          <div className="transition-option">
            <label>{t('eventSettings.activate') || 'Activate'}</label>
            <label className="switch">
              <input
                type="checkbox"
                checked={watermarkEnabled}
                onChange={() => setWatermarkEnabled(!watermarkEnabled)}
                // désactive l’interaction si non autorisé (P0/P1/P5)
                disabled={!SUBSCRIPTION_ACCESS.watermark?.includes(subscription)}
              />
              <span className="slider round"></span>
            </label>
          </div>
        ))}
 

      </div>
    </>
  );
}

export default SettingsMenu;